import { useCallback, useEffect, useMemo, useState } from "react";

import { unwrapResult } from "@reduxjs/toolkit";
import InfiniteScroll from "react-infinite-scroll-component";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DisplayMode } from "@/constants/app.constants";
import { useAppDispatch, useAppSelector } from "@/redux/store";
import { Loader2 } from "lucide-react";

import { getGroupMembersListThunk, groupActions } from "../../group";
import { IMemberGroup } from "../../types/group.type";
import CardUser from "./component/CardUser";
import { UserDetail } from "./component/UserDetail";
import {
  GroupUserRoleEnums,
  SortGroupUserRoleEnums,
} from "../../constants/group.constant";

const ITEMS_PER_PAGE = 30;

const Member = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();
  const { data: groupInfo, membersList } = useAppSelector(
    (state) => state.group
  );

  // States
  const [page, setPage] = useState(1);
  const [isLoadMore, setIsLoadMore] = useState(true);
  const [displayMode] = useState<DisplayMode>(DisplayMode.LIST);

  const getRolePriority = (role: GroupUserRoleEnums): number => {
    return SortGroupUserRoleEnums[role] || 99;
  };

  // Sorted members list by role
  const sortedMembersList = useMemo(() => {
    return [...membersList].sort((a, b) => {
      const priorityA = getRolePriority(a.role);
      const priorityB = getRolePriority(b.role);

      // If same role, sort by userId to maintain consistency
      if (priorityA === priorityB) {
        return a.userId - b.userId;
      }
      return priorityA - priorityB;
    });
  }, [membersList]);

  // URL params với memoization
  const urlParams = useMemo(
    () => ({
      idUser: searchParams.get("idUser")
        ? Number(searchParams.get("idUser"))
        : null,
    }),
    [searchParams]
  );

  // Update URL params helper
  const updateUrlParams = useCallback(
    (key: string, value: string | null) => {
      const newParams = new URLSearchParams(window.location.search);

      if (value === null || value === "default") {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }

      navigate(`${window.location.pathname}?${newParams.toString()}`);
    },
    [navigate]
  );

  // Fetch members with proper pagination
  const fetchGroupMembers = useCallback(
    async (
      pageNumber: number,
      isLoadMore = false,
      currentMembers: IMemberGroup[] = []
    ) => {
      if (!groupInfo) return;

      try {
        const result = await dispatch(
          getGroupMembersListThunk({
            pageNumber,
            pageSize: ITEMS_PER_PAGE,
            groupId: groupInfo.id,
          })
        );

        const unwrappedResult = unwrapResult(result);

        // If it's load more, append to existing list
        if (isLoadMore) {
          dispatch(
            groupActions.updateMembersList([
              ...currentMembers,
              ...unwrappedResult,
            ])
          );
        }

        // Check if we should disable load more
        if (
          !unwrappedResult.length ||
          unwrappedResult.length < ITEMS_PER_PAGE
        ) {
          setIsLoadMore(false);
          return;
        }

        // Reset load more when it's not a pagination request
        if (!isLoadMore) {
          setIsLoadMore(true);
          setPage(1);
        }
      } catch (error) {
        console.error("Error fetching group members:", error);
        setIsLoadMore(false);
      }
    },
    [dispatch, groupInfo]
  );

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (membersList.length >= (groupInfo?.memberCounter ?? 0)) {
      return;
    }
    const nextPage = page + 1;
    setPage(nextPage);
    fetchGroupMembers(nextPage, true, membersList);
  }, [page, fetchGroupMembers, membersList, groupInfo]);

  // Handle user selection
  const handleSetUserId = useCallback(
    (id: number) => {
      updateUrlParams("idUser", String(id));
    },
    [updateUrlParams]
  );

  // Handle close user detail
  const handleCloseUserDetail = useCallback(() => {
    updateUrlParams("idUser", null);
  }, [updateUrlParams]);

  // Initial fetch và fetch khi groupInfo thay đổi
  useEffect(() => {
    if (groupInfo) {
      // Reset pagination when component mounts or groupInfo changes
      setPage(1);
      setIsLoadMore(true);
      // Clear current list before fetching new data
      dispatch(groupActions.updateMembersList([]));
      fetchGroupMembers(1, false, []);
    }
  }, [groupInfo, dispatch, fetchGroupMembers]);

  // Early return nếu không có groupInfo
  if (!groupInfo) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        <span>Đang tải thông tin nhóm...</span>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-280px)] bg-white">
      {/* Header */}
      <div className="z-50">
        <div className="container">
          <div className="py-4 flex">
            <p className="font-semibold text-secondary-900 mr-2">
              {groupInfo.memberCounter} Thành viên
            </p>
          </div>
        </div>
      </div>

      {/* Members List */}
      <div
        id="members-list"
        className="container"
      >
        <InfiniteScroll
          style={{
            height: "unset",
            overflow: "unset",
          }}
          className="px-0"
          dataLength={membersList.length}
          next={handleLoadMore}
          hasMore={isLoadMore}
          loader={
            <div className="flex items-center justify-center mt-6">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              <span className="text-sm text-secondary-600">
                Đang tải thêm...
              </span>
            </div>
          }
          endMessage={
            membersList.length > 0 && (
              <div className="text-center py-4">
                <span className="text-sm text-secondary-600">
                  Đã hiển thị tất cả thành viên
                </span>
              </div>
            )
          }
        >
          <div
            className={
              displayMode === DisplayMode.GRID
                ? "grid sm:grid-cols-3 lg:grid-cols-5 2xl:grid-cols-6 gap-4"
                : ""
            }
          >
            {sortedMembersList.map((member, index) => (
              <CardUser
                member={member}
                displayMode={displayMode}
                key={`${member.userId}-${index}`}
                groupId={member.userId}
                setId={handleSetUserId}
              />
            ))}
          </div>

          {/* Empty state */}
          {membersList.length === 0 && (
            <div className="text-center py-8">
              <p className="text-secondary-600">Không có thành viên nào</p>
            </div>
          )}
        </InfiniteScroll>
      </div>

      {/* User Detail Modal */}
      <UserDetail
        userId={urlParams.idUser}
        close={handleCloseUserDetail}
      />
    </div>
  );
};

export default Member;
